import { DOCUMENT } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { AVAILABLE_LANGS, Lang } from './core/i18n/translations';

/**
 * Root shell component. Hosts the router outlet and keeps `document.documentElement.lang`
 * in sync with the `?lang=` query parameter (or browser locale).
 */
@Component({
  selector: 'app-root',
  template: '<router-outlet></router-outlet>',
  imports: [RouterOutlet],
})
export class AppComponent {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  /** Active language derived from the URL or browser settings. */
  private readonly queryLang = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.readLangFromUrl()),
      startWith(this.readLangFromUrl()),
    ),
    { initialValue: this.readLangFromUrl() },
  );

  constructor() {
    effect(() => {
      const lang = this.queryLang();
      this.document.documentElement.lang = lang;
    });
  }

  /**
   * Resolves UI language from `?lang=` or falls back to browser / `pl`.
   *
   * @returns BCP 47 language code supported by the panel.
   */
  private readLangFromUrl(): Lang {
    const lang = new URLSearchParams(this.document.location?.search ?? '')
      .get('lang')
      ?.substring(0, 2);

    if (lang && AVAILABLE_LANGS.includes(lang as Lang)) {
      return lang as Lang;
    }

    const browserLang =
      this.document.defaultView?.navigator.language?.substring(0, 2) ?? 'pl';
    return AVAILABLE_LANGS.includes(browserLang as Lang)
      ? (browserLang as Lang)
      : 'pl';
  }
}
